import { useSpotUser } from "@/utils/spotUser";
import { useCallback, useEffect, useMemo } from "react";
import { DATA_KEYS, useRemoteData } from "@/utils/useRemoteData";
import { keepPreviousData } from "@tanstack/react-query";
import { getCached, setCache, CACHE_KEYS } from "@/utils/queryCache";
import type { UserPermissionsResponse } from "@/generated/types.gen";
import { P } from "@/constants/permissions";

/**
 * Hook to fetch and check user permissions at both tenant and workspace levels
 *
 * This hook provides granular permission checking based on the user's role
 * and any custom permission overrides. It caches permissions and provides
 * helper functions for common permission checks.
 *
 * @example
 * ```tsx
 * const { hasPermission, hasAnyPermission, isLoading } = usePermissions();
 *
 * // Check workspace-level permission
 * if (hasPermission('resource:edit')) {
 *   // Show create flow button
 * }
 *
 * // Check tenant-level permission
 * if (hasPermission('org:admin', 'tenant')) {
 *   // Show user management
 * }
 *
 * // Check if user has any of the specified permissions
 * if (hasAnyPermission(['resource:edit', 'resource:delete'])) {
 *   // Show edit actions
 * }
 * ```
 */
export function usePermissions(groupId?: string) {
  const { spotUser, spotGroup } = useSpotUser();

  const effectiveGroupId = groupId || spotGroup?.id;

  // Seed from localStorage so the sidebar renders immediately on hard refresh.
  const cachedPermissions = useMemo(
    () =>
      effectiveGroupId
        ? getCached<UserPermissionsResponse>(
            CACHE_KEYS.PERMISSIONS(effectiveGroupId)
          )
        : undefined,
    [effectiveGroupId]
  );

  // Fetch permissions from the backend.
  // Wait for spotGroup to be available before fetching — otherwise
  // group_id=null returns no group_permissions and the route hook
  // redirects to the wrong landing page after login.
  const {
    data: permissions,
    isLoading: isQueryLoading,
    isFetched,
    isPlaceholderData,
    error,
  } = useRemoteData(
    DATA_KEYS.MY_PERMISSIONS,
    {
      query: effectiveGroupId
        ? { group_id: effectiveGroupId }
        : { group_id: null },
    },
    {
      enabled: !!spotUser?.id && !!effectiveGroupId,
      staleTime: 5 * 60 * 1000,
      placeholderData: keepPreviousData,
      // Seed from localStorage for instant paint, but mark it stale so a
      // fresh fetch always fires — otherwise a role change followed by a
      // refresh serves the old permission map for the whole staleTime.
      initialData: cachedPermissions,
      initialDataUpdatedAt: 0,
      retry: 1,
    }
  );

  // Persist fresh permissions to localStorage so next refresh is instant.
  useEffect(() => {
    if (permissions && effectiveGroupId) {
      setCache(CACHE_KEYS.PERMISSIONS(effectiveGroupId), permissions);
    }
  }, [permissions, effectiveGroupId]);

  // Treat as loading while user or group aren't resolved yet
  const isLoading = isQueryLoading || !spotUser?.id || !effectiveGroupId;

  /**
   * Check if the user has a specific permission
   *
   * @param permission - The permission string to check (e.g., 'flows_create')
   * @param level - Whether to check tenant-level or workspace-level permissions
   * @returns true if the user has the permission, false otherwise
   */
  const hasPermission = useCallback(
    (permission: string, level: "tenant" | "group" = "group"): boolean => {
      if (!permissions) return false;

      if (level === "tenant") {
        return permissions.tenant_permissions?.[permission] === true;
      }

      // No group membership — deny by default
      if (
        permissions.group_permissions === null ||
        permissions.group_permissions === undefined
      ) {
        return false;
      }

      return permissions.group_permissions[permission] === true;
    },
    [permissions]
  );

  const hasPermissions = useCallback(
    (
      perms: string[],
      level: "tenant" | "group" = "group",
      type: "any" | "all" = "all"
    ): boolean => {
      return type === "any"
        ? perms.some((p) => hasPermission(p, level))
        : perms.every((p) => hasPermission(p, level));
    },
    [hasPermission]
  );
  /**
   * Check if the user has ANY of the specified permissions
   *
   * @param perms - Array of permission strings to check
   * @param level - Whether to check tenant-level or workspace-level permissions
   * @returns true if the user has at least one of the permissions
   */
  const hasAnyPermission = useCallback(
    (perms: string[], level: "tenant" | "group" = "group"): boolean => {
      return perms.some((p) => hasPermission(p, level));
    },
    [hasPermission]
  );

  /**
   * Check if the user has ALL of the specified permissions
   *
   * @param perms - Array of permission strings to check
   * @param level - Whether to check tenant-level or workspace-level permissions
   * @returns true if the user has all of the permissions
   */
  const hasAllPermissions = useCallback(
    (perms: string[], level: "tenant" | "group" = "group"): boolean => {
      return perms.every((p) => hasPermission(p, level));
    },
    [hasPermission]
  );

  /**
   * Get all permissions for a specific level
   *
   * @param level - Whether to get tenant-level or workspace-level permissions
   * @returns Object mapping permission strings to boolean values
   */
  const getPermissions = useCallback(
    (level: "tenant" | "group" = "group"): Record<string, boolean> => {
      if (!permissions) return {};

      return level === "tenant"
        ? permissions.tenant_permissions
        : (permissions.group_permissions ?? {});
    },
    [permissions]
  );

  const isOrgAdmin = hasPermission(P.ORG_ADMIN, "tenant");
  const isWorkspaceAdmin = hasPermission(P.WORKSPACE_ADMIN, "group");

  return {
    /** All permission data including tenant and group permissions */
    permissions,
    /** Check if a single permission is granted */
    hasPermission,
    /** Check if any or all of the specified permissions are granted */
    hasPermissions,
    /** Check if any of the specified permissions are granted */
    hasAnyPermission,
    /** Check if all of the specified permissions are granted */
    hasAllPermissions,
    /** Get all permissions for a specific level */
    getPermissions,
    /** Platform (organization) admin */
    isOrgAdmin,
    /** Admin of the active workspace */
    isWorkspaceAdmin,
    /** Admin at either level — for actions that affect a whole workspace */
    isAdmin: isOrgAdmin || isWorkspaceAdmin,
    /** The name of the assigned role, if any */
    roleName: permissions?.role_name ?? null,
    /** Whether the user has custom permission overrides beyond their role */
    hasCustomOverrides: permissions?.has_custom_overrides ?? false,
    /** Whether permissions are currently being loaded */
    isLoading,
    /** Fetched for this workspace, not the localStorage copy or the
     *  previous workspace's kept while switching */
    isFresh: isFetched && !isPlaceholderData,
    /** Error if permission fetch failed */
    error,
  };
}
